import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Alert, FormGroup, FormControlLabel, Checkbox, FormHelperText } from "@mui/material";

import { useAppSelector } from "../../../hooks/useAuth";
import useRBAC from "../../../hooks/useRBAC";
import { RootState } from "../../../store";
import apiService from "../../../services/api";
import useHubs from "../../../hooks/useHubs";
import useRoleBasedHubFiltering from "../../../hooks/useRoleBasedHubFiltering";
import { useFormValidation, commonValidationRules, ValidationRules } from "../../../components/common/FormValidation";
import { useCommonData } from "./CommonDataHooks";
import { FormSection, TextInput, SelectInput, SearchableSelectInput, MobileInput, FileUploadInput } from "../../../components/forms";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import CustomLoading from "../../../components/common/CustomLoading";
import useCodeCommonByCategory from "../../../hooks/useCodeCommonByCategory";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

const MULTI_HUB_ROLES = ["Admin", "Finance", "Hub Cluster Manager", "Support Admin"];



const DateField: React.FC<{
  label: string;
  field: string;
  value: string;
  onChange: (field: string, value: string) => void;
  error?: string | null;
  required?: boolean;
}> = ({ label, field, value, onChange, error, required }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DesktopDatePicker
      label={label}
      format="DD/MM/YYYY"
      value={value ? dayjs(value) : null}
      onChange={(newValue: any) => {
        const date = newValue ? (newValue.format ? newValue : dayjs(newValue)) : null;
        onChange(field, date?.format("YYYY-MM-DD") || "")
      }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          required: required,
          error: !!error,
          helperText: error,
        },
      }}
    />
  </LocalizationProvider>
);

const transformUserData = (userData: any, id?: string, getDisplayNameByCodeValue?: (codeValue: string ) => string, hubOptions?: Array<{ hubmasterid: number; hubname: string }>) => {
  let statusDisplay = "";
  if (userData?.staffstatus !== undefined && getDisplayNameByCodeValue) {
    statusDisplay = getDisplayNameByCodeValue(userData.staffstatus);
  }
  const isMultiHubRole = userData?.rolename && MULTI_HUB_ROLES.includes(userData.rolename);
  const roleHubRelationList = userData?.roleHubRelationList && Array.isArray(userData.roleHubRelationList) ? userData.roleHubRelationList : [];

  return {
    profilePhoto: userData?.profilephoto || "",
    fullName: userData?.name || "",
    dateOfBirth: userData?.dob ? dayjs(userData.dob).format("YYYY-MM-DD") : "",
    mobileNo: userData?.phonenumber || "",
    mobileCode: "+91",
    email: userData?.emailid || "",
    role: userData?.rolename || "",
    assignedHub: (() => {
      if (isMultiHubRole && roleHubRelationList.length > 0 && hubOptions) {
        const names = roleHubRelationList
          .map((r: { hubid: number }) => hubOptions.find((h) => h.hubmasterid === r.hubid)?.hubname)
          .filter(Boolean);
        return names.join(", ");
      }
      const hubId = userData?.hubid;
      if (hubId && hubOptions) {
        const hub = hubOptions.find((h) => h.hubmasterid === hubId);
        if (hub) return hub.hubname;
      }
      return userData?.hubname || "";
    })(),
    designation: userData?.employmentdetail?.empdesignation || "",
    joiningDate: userData?.employmentdetail?.joiningdate ? dayjs(userData.employmentdetail.joiningdate).format("YYYY-MM-DD") : "",
    status: statusDisplay,
    emergencyContact: userData?.employmentdetail?.emergencycontact || "",
    emergencyContactCode: "+91",
    specimenESignature: userData?.employmentdetail?.specimen_e_signature || "",
    username: userData?.username || "",
    password: "",
    confirmPassword: "",
    firstName: userData?.name?.split(" ")[0] || "",
    lastName: userData?.name?.split(" ").slice(1).join(" ") || "",
    id: userData?.userid?.toString() || id || "",
  };
};

const EditStaffPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { getCreatableRoleNames } = useRBAC();

  const [formData, setFormData] = useState<Record<string, string>>(transformUserData({}, undefined, undefined, []));
  const [assignedHubIds, setAssignedHubIds] = useState<number[]>([]);
  const [originalUserData, setOriginalUserData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { roles, rolesLoading, fetchRoles, uploadingImages, handleImageUpload, fetchUserData } =
    useCommonData();
  const { statusOptions, loading: statusLoading, getDisplayNameByCodeValue, getCodeValueByDisplayName } = useCodeCommonByCategory('StaffStatus');
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { filterHubOptions } = useRoleBasedHubFiltering();

  const editStaffValidationRules: ValidationRules = {
    ...commonValidationRules,
    assignedHub: {
      required: false,
      custom: (value: string, formDataObj: any) => {
        if (formDataObj?.role && MULTI_HUB_ROLES.includes(formDataObj.role)) return undefined;
        if (!value || String(value).trim() === "") return "Assigned hub is required";
        return undefined;
      },
    },
  };

  const { isValid, clearFieldError, getFieldError, validateFormData } = useFormValidation(
    formData,
    editStaffValidationRules
  );

  const filteredHubOptions = filterHubOptions(hubOptions);

  useEffect(() => {
    if (error === "Please fill all required fields") {
      const requiredFields = [
        "profilePhoto", "fullName", "dateOfBirth", "mobileNo", "email",
        "role",
        "username", "status", "emergencyContact"
      ];
      const allFilled = requiredFields.every((f) => {
        const fieldValue = formData[f];
        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== "";
      });
      const hubFilled =
        formData.role && MULTI_HUB_ROLES.includes(formData.role)
          ? assignedHubIds.length > 0
          : (formData.assignedHub !== undefined && formData.assignedHub !== null && String(formData.assignedHub).trim() !== "");

      if (allFilled && hubFilled) {
        setError(null);
      }
    }
  }, [formData, error, assignedHubIds]);

  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  const isMultiHubRole = Boolean(formData.role && MULTI_HUB_ROLES.includes(formData.role));

  const handleInputChange = (field: string, value: string) => {
    if (field === "role") {
      if (MULTI_HUB_ROLES.includes(value)) {
        setAssignedHubIds((prev) => prev);
        setFormData((prev) => ({ ...prev, assignedHub: "", [field]: value }));
      } else {
        setAssignedHubIds([]);
        setFormData((prev) => ({ ...prev, [field]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    clearFieldError(field);
  };

  const handleHubCheckboxChange = (hubId: number, checked: boolean) => {
    setAssignedHubIds((prev) => (checked ? [...prev, hubId] : prev.filter((id) => id !== hubId)));
    clearFieldError("assignedHub");
  };

  const handleImageUploadWithCallbacks = async (field: string, file: File) => {
    await handleImageUpload(
      field,
      file,
      (f, url) => {
        setFormData((prev) => ({ ...prev, [f]: url }));
        clearFieldError(f);
      },
      (err) => setError(err)
    );
  };

  const mapFormDataToApiPayload = () => {
    const empCode = `EMP-${Date.now()}`;
    const statusCodeValue = formData.status ? getCodeValueByDisplayName(formData.status) : null;
    const selectedRole = roles.find((r) => r.rolename === formData.role);
    const roleId = selectedRole?.roleid ?? null;
    const isMulti = Boolean(roleId && formData.role && MULTI_HUB_ROLES.includes(formData.role));

    type RoleHubItem = { rolehubrelationid: number; userid: number; roleid: number; hubid: number };
    let hubId: number | null = null;
    let hubname = formData.assignedHub;
    let roleHubRelationList: RoleHubItem[];

    if (isMulti && assignedHubIds.length > 0) {
      const existingList = originalUserData?.roleHubRelationList as RoleHubItem[] | undefined;
      roleHubRelationList = assignedHubIds.map((hubid, index) => {
        const existing = existingList?.find((r) => r.hubid === hubid);
        return {
          rolehubrelationid: existing?.rolehubrelationid ?? 0,
          userid: id ? parseInt(id, 10) : 0,
          roleid: roleId ?? 0,
          hubid,
        };
      });
      hubId = roleHubRelationList[0]?.hubid ?? null;
      const names = assignedHubIds
        .map((hid) => hubOptions.find((h) => h.hubmasterid === hid)?.hubname)
        .filter(Boolean);
      hubname = names.join(", ");
    } else {
      const selectedHub = hubOptions.find((h) => h.hubname === formData.assignedHub);
      hubId = selectedHub ? selectedHub.hubmasterid : null;
      const existingModel = originalUserData?.roleHubRelationModel;
      roleHubRelationList = [
        {
          rolehubrelationid: existingModel?.rolehubrelationid ?? 0,
          userid: id ? parseInt(id, 10) : 0,
          roleid: roleId ?? 0,
          hubid: hubId ?? 0,
        },
      ];
    }

    const roleHubRelationModel = roleHubRelationList[0] ?? {
      rolehubrelationid: originalUserData?.roleHubRelationModel?.rolehubrelationid ?? 0,
      userid: id ? parseInt(id, 10) : 0,
      roleid: roleId ?? 0,
      hubid: hubId ?? 0,
    };

    const normalizeUrl = (url: string) =>
      url && !url.startsWith("http") ? url.replace(/^\/+/, "") : url;

    const convertDateToISO = (dateStr: string): string | null => {
      if (!dateStr) return null;
      const parsed = dayjs(dateStr, "YYYY-MM-DD");
      if (!parsed.isValid()) return null;
      return parsed.format("YYYY-MM-DDTHH:mm:ss");
    };

    const dobISO = convertDateToISO(formData.dateOfBirth);
    const joiningDateISO = convertDateToISO(formData.joiningDate);

    return {
      userid: id ? parseInt(id, 10) : 0,
      name: formData.fullName,
      emailid: formData.email,
      password: formData.password || undefined,
      profilephoto: normalizeUrl(formData.profilePhoto),
      phonenumber: formData.mobileNo,
      userstatus: 1,
      bannedreason: "",
      roleid: roleId,
      rolename: formData.role,
      dob: dobISO,
      hubid: hubId,
      empcode: formData.id ? `EMP-${formData.id}` : empCode,
      username: formData.username,
      hubname,
      staffstatus: statusCodeValue || 0,
      userjoiningdate: originalUserData?.userjoiningdate || (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T00:00:00`;
      })(),
      roleHubRelationList,
      roleHubRelationModel,
      employmentdetail: {
        empdetailid: originalUserData?.employmentdetail?.empdetailid || 0,
        userid: id ? parseInt(id, 10) : 0,
        empdesignation: formData.designation,
        joiningdate: joiningDateISO,
        status: true,
        emergencycontact: formData.emergencyContact,
        specimen_e_signature: formData.specimenESignature,
      },
    };
  };

  const handleSave = async () => {
    if (!originalUserData) return setError("Please wait, data is still loading.");

    setError(null);
    setSuccess(null);

    if (!validateFormData(formData)) {
      setError("Please fill all required fields");
      return;
    }
    if (isMultiHubRole && assignedHubIds.length === 0) {
      setError("Please select at least one hub.");
      return;
    }

    setLoading(true);

    try {
      await apiService.createOrUpdateUser(mapFormDataToApiPayload());
      setSuccess("Staff member updated successfully!");
      setTimeout(() => {
        setSuccess(null);
        navigate("/staff-management", { state: location.state });
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to update staff member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadBaseData = async () => {
      try {
        if (id) {
          setLoading(true);
          const userData = await fetchUserData(id);
          if (userData) {
            setOriginalUserData(userData);
          }
        }
        await fetchRoles();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    loadBaseData();
  }, [id]);

  useEffect(() => {
    if (!originalUserData) return;
    setFormData(transformUserData(originalUserData, id, getDisplayNameByCodeValue, hubOptions));
    const list = originalUserData?.roleHubRelationList;
    const isMulti = originalUserData?.rolename && MULTI_HUB_ROLES.includes(originalUserData.rolename);
    
    if (isMulti && list && Array.isArray(list) && list.length > 0) {
      setAssignedHubIds(list.map((r: { hubid: number }) => r.hubid));
    } else if (isMulti && originalUserData?.hubname && hubOptions.length > 0) {
      const hubnameString = originalUserData.hubname;
      if (hubnameString && typeof hubnameString === 'string' && hubnameString.includes(',')) {
        const hubNames = hubnameString.split(',').map(name => name.trim()).filter(Boolean);
        const matchedHubIds = hubNames
          .map(name => hubOptions.find(h => h.hubname === name)?.hubmasterid)
          .filter((id): id is number => id !== undefined);
        if (matchedHubIds.length > 0) {
          setAssignedHubIds(matchedHubIds);
        } else if (originalUserData?.hubid == null) {
          setAssignedHubIds([]);
        } else {
          setAssignedHubIds([originalUserData.hubid]);
        }
      } else if (originalUserData?.hubid == null) {
        setAssignedHubIds([]);
      } else {
        setAssignedHubIds([originalUserData.hubid]);
      }
    } else if (isMulti && originalUserData?.hubid == null) {
      setAssignedHubIds([]);
    } else if (isMulti) {
      setAssignedHubIds([originalUserData.hubid]);
    } else {
      setAssignedHubIds([]);
    }
  }, [originalUserData, id, getDisplayNameByCodeValue, hubOptions]);

  const renderAttachments = (field: string, label: string, url?: string) => {
    if (!url) return null;
    return (
      <Col xs={12} sm={6} md={4}>
        <ImagePreviewCard
          title={label}
          value={url}
          resolveSrc={resolvePreviewSrc}
          size={200}
          defaultImage={DefaultImage}
          onDelete={() => setFormData((prev) => ({ ...prev, [field]: "" }))}
        />
      </Col>
    );
  };

  if (loading && !originalUserData) {
    return <CustomLoading message="Fetching Staff Details..." />;
  }

  return (
    <Container fluid>
      {error && (
        <Alert severity="error" className="mb-3" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" className="mb-3" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <FormSection title="Identity & Contact">
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <FileUploadInput
              field="profilePhoto"
              formData={formData}
              onChange={handleInputChange}
              onImageUpload={handleImageUploadWithCallbacks}
              uploading={uploadingImages.profilePhoto}
              required
              error={getFieldError("profilePhoto")}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="fullName"
              formData={formData}
              onChange={handleInputChange}
              label="Full Name"
              required
              placeholder="Enter full name"
              error={getFieldError("fullName")}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <DateField
              label="Date of Birth"
              field="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              error={getFieldError("dateOfBirth")}
              required
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <MobileInput
              field="mobileNo"
              formData={formData}
              onChange={handleInputChange}
              placeholder="Enter mobile number"
              required
              error={getFieldError("mobileNo")}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="email"
              formData={formData}
              onChange={handleInputChange}
              label="Email"
              type="email"
              required
              placeholder="Enter email"
              error={getFieldError("email")}
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Role & Assignment">
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="role"
              formData={formData}
              onChange={handleInputChange}
              label="Role"
              required
              disabled={rolesLoading}
              options={roles
                .filter(
                  (r) =>
                    r.rolename !== "SuperAdmin" &&
                    r.rolename !== "User" &&
                    getCreatableRoleNames().includes(r.rolename)
                )
                .map((r) => r.rolename)}
              error={getFieldError("role")}
            />
          </Col>
          <Col xs={12} md={6} lg={isMultiHubRole ? 12 : 3}>
            {isMultiHubRole ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Assigned Hubs <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <FormGroup row sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {filteredHubOptions.map((hub: { hubmasterid: number; hubname: string }) => (
                    <FormControlLabel
                      key={hub.hubmasterid}
                      control={
                        <Checkbox
                          checked={assignedHubIds.includes(hub.hubmasterid)}
                          onChange={(e) => handleHubCheckboxChange(hub.hubmasterid, e.target.checked)}
                          disabled={hubsLoading}
                          size="small"
                        />
                      }
                      label={hub.hubname}
                    />
                  ))}
                </FormGroup>
                {getFieldError("assignedHub") && (
                  <FormHelperText error>{getFieldError("assignedHub")}</FormHelperText>
                )}
              </Box>
            ) : (
              <SearchableSelectInput
                field="assignedHub"
                formData={formData}
                onChange={handleInputChange}
                label="Assigned Hub"
                required
                disabled={hubsLoading}
                placeholder="Search and select hub"
                options={
                  hubsLoading
                    ? []
                    : filteredHubOptions.map((hub: { hubname: string }) => hub.hubname)
                }
                error={getFieldError("assignedHub")}
              />
            )}
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Employment & Operations">
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <TextInput field="designation" formData={formData} onChange={handleInputChange} label="Designation" placeholder="Enter designation" />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <DateField
              label="Joining Date"
              field="joiningDate"
              value={formData.joiningDate}
              onChange={handleInputChange}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="status"
              formData={formData}
              onChange={handleInputChange}
              label="Status"
              required
              disabled={statusLoading}
              options={statusOptions}
              error={getFieldError("status")}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <MobileInput 
              field="emergencyContact" 
              formData={formData} 
              onChange={handleInputChange} 
              placeholder="Emergency Contact"
              required
              error={getFieldError("emergencyContact")}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput field="specimenESignature" formData={formData} onChange={handleInputChange} label="Specimen E-Signature" placeholder="Enter specimen signature" />
          </Col>
        </Row>
      </FormSection>

      <FormSection title="System Access">
        <Row className="g-3">
          <Col xs={12} md={6} lg={3}>
            <TextInput field="username" formData={formData} onChange={handleInputChange} label="User Name" required placeholder="Enter user name" error={getFieldError("username")} />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput field="password" formData={formData} onChange={handleInputChange} label="Password" type="password" placeholder="Enter new password (leave blank to keep current)" error={getFieldError("password")} />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput field="confirmPassword" formData={formData} onChange={handleInputChange} label="Confirm Password" type="password" placeholder="Confirm new password" error={getFieldError("confirmPassword")} />
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Attachments">
        <Row className="g-3">
          {renderAttachments("profilePhoto", "Profile Image", formData.profilePhoto)}
          {!formData.profilePhoto && (
            <Col xs={12}>
              <Box sx={{ p: 4, textAlign: "center", backgroundColor: "#f8f9fa", borderRadius: 2, border: "2px dashed #dee2e6" }}>
                <Typography variant="body1" sx={{ color: "#6c757d" }}>
                  No attachments uploaded yet
                </Typography>
              </Box>
            </Col>
          )}
        </Row>
      </FormSection>

      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          disabled={loading || !isValid || (isMultiHubRole && assignedHubIds.length === 0)}
          onClick={handleSave}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": { backgroundColor: "rgba(13,44,90,0.5)" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" /> Updating...
            </Box>
          ) : (
            "Update"
          )}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate("/staff-management", { state: location.state })}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": { borderColor: "rgba(13,44,90,1)", backgroundColor: "rgba(13,44,90,0.04)" },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default EditStaffPage;
